import { createApp, h } from 'vue';
import type { App } from 'vue';
import type { I18n } from 'vue-i18n';
import { createI18n } from 'vue-i18n';
import { getGlobalNamespace, interceptAxios } from '@patsnap-utils/passport';
import { setPostByFormPassport, psAxios } from '@patsnap/common-ps-http';
import type Passport from '@patsnap-utils/passport/src/passport';
import GoogleSignInPlugin from 'vue3-google-signin'
import { cn, en, jp, tw } from './src/locales'
import { getCurrentLanguage } from '@eureka/language-tool'
import type { InitFormParams } from './src/login-us/types/accounts';
import { handleThirdPartyLoginErrors } from './src/login-us/utils/third-login-error-handler';
import { REGION } from '@eureka/utils/environment';
import { DeployEnv } from '@patsnap-utils/biz';

export interface OpenCallbackParams {
    token: string
    refresh_token: string
    passport: Passport
}

export enum DialogMode {
    LOGIN = 'login',
    REGISTER = 'register'
}

export enum WUJIE_PROPS_USER_ENTRY_METHOD {
    /**
     * 微信登录注册
     */
    Wechat = 'wechat',
    /**
     * 手机号登录注册
     */
    SMS = 'sms',
    /**
     * 账号密码登录
     */
    Account = 'account',
    /**
     * sso carsi/saml登录注册
     */
    SSO = 'sso',
    /**
     * google登录
     */
    Google = 'google',
    /**
     * 免费注册
     */
    FreeRegister = 'freeRegister'
}

export interface OpenFallbackParams {
    close?: boolean
    error?: Error
}

export enum SDK_ENV {
    CN = 'CN',
    US = 'US',
    EU = 'EU'
}

export interface LoginSDKOptions {
    i18n: I18n
    channels: Channels
}

export interface OpenOptions {
    withoutClose?: boolean
    initFormParams?: InitFormParams
    isSignup?: boolean
    silence?: boolean
}

export interface Channels {
    from?: string
    from_page?: string
    channel_source?: string
    activity_type?: string
    inviter_id?: string
    invitation_code?: string
    anonymousLogin?: boolean
}

export enum EMPOWER_TYPE {
    AIR_HOME = 'AIR_HOME',
    FMEA = 'FMEA',
    MONITOR = 'MONITOR',
    TECH_RESEARCH = 'TECH_RESEARCH',
    TECH_MIND = 'TECH_MIND',
    MATERIAL_MIND = 'MATERIAL_MIND',
    HISTORY = 'HISTORY'
}

export enum CHANNEL_SOURCE {
    AIR_EUREKA = 'dialog_signup_eureka'
}

export class LoginSDK {
    private i18n: I18n | undefined = undefined;
    private env: SDK_ENV | undefined = undefined;
    private channels: Channels = {};
    private _div: HTMLDivElement | null = null;
    private _html: HTMLHtmlElement | null = null;
    private _app: App<Element> | null = null;

    constructor(options?: LoginSDKOptions | undefined) {
        if (options?.i18n) {
            this.i18n = options.i18n
        }
        if (options?.channels) {
            this.channels = options.channels
        }

        return this
    }

    /**
     * 创建独立的 i18n 实例
     * 避免与其他模块的 i18n 实例冲突
     */
    private createI18n(): I18n {
        const locale = getCurrentLanguage();
        const messages = {
            cn,
            en,
            jp,
            tw,
        };

        return createI18n({
            legacy: false,
            locale,
            fallbackLocale: 'en',
            messages,
        });
    }

    /**
     * 获取或创建 i18n 实例
     * 始终使用独立的 i18n 实例，不依赖外部 i18n
     */
    private getOrCreateI18n(): I18n {
        if (!this.i18n) {
            this.i18n = this.createI18n();
        }
        return this.i18n;
    }

    /**
     * 确保 env 已初始化
     * 自动检测区域并设置环境
     */
    private ensureEnvInitialized(): void {
        if (!this.env) {
            this.env = [DeployEnv.US, DeployEnv.EU].includes(REGION)
                ? SDK_ENV.US
                : SDK_ENV.CN;
        }
    }

    getI18n() {
        return this.getOrCreateI18n()
    }

    setChannels(channels: Channels) {
        this.channels = channels
        return this
    }

    async getLoginUI() {
        return this.env === SDK_ENV.CN
            ? await import('./src/LoginCN.vue').then(m => m.default)
            : await import('./src/LoginUSNext.vue').then(m => m.default)
    }

    private _createContainer() {
        if (this._div) {
            this._div.remove()
        }
        this._div = document.createElement('div');
        this._div.setAttribute('data-theme', 'patsnapRD');
        this._html = window.parent.document.querySelector('html')
        this._html?.setAttribute('style', 'overflow:hidden')
        this._div.style.cssText = 'display:flex;align-items:center;justify-content:center;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;';
        document.body.appendChild(this._div);
        return [this._div, this._html];
    }

    private _destroyContainer() {
        this._div?.remove();
        this._app?.unmount();
        this._html?.removeAttribute('style');
        return this;
    }

    private async _prepareTokenBase() {
        const { singletonPassport: passport } = getGlobalNamespace()
        const token = await passport.getAccessToken()
        if (!psAxios.psInterceptors.passportRequestInterceptor) {
            interceptAxios(psAxios);
        }
        setPostByFormPassport(passport)
        return {
            token,
            passport,
        }
    }

    private _registerGoogleLogin(app: App) {
        app.use(GoogleSignInPlugin, {
        // @ts-ignore
            clientId: import.meta.env.VITE_GOOGLE_LOGIN_CLIENT_ID,
        });
    }

    async open(options?: OpenOptions) {
        this.ensureEnvInitialized();
        const i18n = this.getOrCreateI18n();
        const [div, html] = this._createContainer();
        if (options?.silence && html && div) {
            div.style.cssText = 'display:flex;position:fixed;bottom:16px;right:16px;z-index:99999;';
            html.removeAttribute('style')
        }
        const component = await this.getLoginUI();
        const isSignup = !localStorage.getItem('_PATSNAP_ACCOUNT');

        const promise = new Promise((resolve: (value: OpenCallbackParams) => void, reject: (value: OpenFallbackParams) => void) => {
            const $vm = this;
            this._app = createApp({
                render() {
                    return h(component, {
                        ...options,
                        isSignup,
                        channels: {
                            from: 'eureka',
                            anonymousLogin: false,
                            ...$vm.channels,
                        },
                        onSuccess: async(data: any) => {
                            const { token, passport } = await $vm._prepareTokenBase()
                            resolve({ ...data, token, passport });
                            $vm.close()
                        },
                        onFailed: (error: Error) => {
                            // eslint-disable-next-line
                            reject({ error });
                        },
                        onClose: () => {
                            // eslint-disable-next-line
                            reject({ close: true });
                            $vm.close()
                        },
                    });
                },
            });
            if (this.env === SDK_ENV.US) {
                this._registerGoogleLogin(this._app)
            }
            this._app.use(i18n)
            this._app.mount(div);
        });
        return promise;
    }

    /**
     * 处理第三方登录错误(linkedin, microsoft)
     */
    handleThirdPartyLoginErrors() {
        if (this.env === SDK_ENV.CN) return;
        const i18n = this.getOrCreateI18n();
        handleThirdPartyLoginErrors(i18n);
    }

    close() {
        this._destroyContainer();
    }
}

export const loginSDK = new LoginSDK();

export function buildQueryChannels(queryObj: Channels | undefined) {
    if (!queryObj) return ''
    const results: string[] = []
    for (const key in queryObj) {
        const value = queryObj[key as keyof Channels]
        if (value !== undefined) {
            results.push(`${key}=${value}`)
        }
    }
    return results.join('&')
}
